import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, SHOP } from "@/constants";

const EASE = [0.16, 1, 0.3, 1];

const INPUT_CLS =
  "h-12 w-full border border-forest/20 bg-transparent px-4 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:ring-1 focus:ring-forest transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", event_type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, email: form.email || null, event_type: form.event_type || null });
      toast.success("Thank you! We will call you back shortly.");
      setForm({ name: "", phone: "", email: "", event_type: "", message: "" });
    } catch {
      toast.error("Something went wrong — please reach us on WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="border-t border-forest/10 bg-[#E8EDE9] py-28 lg:py-36">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-sage"
          >
            Visit or Write
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-serif text-4xl font-medium tracking-tight text-forest md:text-6xl"
          >
            Let's plan something <span className="italic text-terracotta">in bloom</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
            className="mt-12 space-y-6"
          >
            <a
              href={`tel:${SHOP.phoneLink}`}
              data-testid="contact-phone-link"
              className="group flex items-center gap-5"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-forest/20 transition-colors duration-300 group-hover:bg-forest group-hover:text-cream">
                <Phone size={18} strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sage">Call us</p>
                <p className="font-serif text-xl italic text-forest">{SHOP.phoneDisplay}</p>
                <p className="text-sm text-sage">{SHOP.phoneAlt}</p>
              </div>
            </a>
            <div className="flex items-center gap-5" data-testid="contact-address">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-forest/20">
                <MapPin size={18} strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sage">Find us</p>
                <p className="font-serif text-xl italic text-forest">{SHOP.address}</p>
              </div>
            </div>
            <div className="flex items-center gap-5" data-testid="contact-hours">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-forest/20">
                <Clock size={18} strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sage">Hours</p>
                <p className="font-serif text-xl italic text-forest">{SHOP.hours}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
            className="mt-12 overflow-hidden border border-forest/15"
            data-testid="contact-map"
          >
            <iframe
              title="Utsav Flowers — Kusum Hotel Complex, Talkie House Road, Dibrugarh"
              src="https://maps.google.com/maps?q=Kusum%20Hotel%20Complex%2C%20Talkie%20House%20Road%2C%20Dibrugarh%2C%20Assam%20786001&t=&z=16&ie=UTF8&iwloc=&output=embed"
              className="h-64 w-full"
              loading="lazy"
            />
          </motion.div>
        </div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
          className="border border-forest/15 bg-cream p-8 md:p-12 lg:col-span-7"
          data-testid="enquiry-form"
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-sage" htmlFor="enquiry-name">
                Your Name *
              </label>
              <input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Full name" className={INPUT_CLS} />
            </div>
            <div>
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-sage" htmlFor="enquiry-phone">
                Phone *
              </label>
              <input id="enquiry-phone" data-testid="enquiry-phone-input" required value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={INPUT_CLS} />
            </div>
            <div>
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-sage" htmlFor="enquiry-email">
                Email
              </label>
              <input id="enquiry-email" data-testid="enquiry-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={INPUT_CLS} />
            </div>
            <div>
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-sage" htmlFor="enquiry-event-type">
                Occasion
              </label>
              <select id="enquiry-event-type" data-testid="enquiry-event-type-select" value={form.event_type} onChange={set("event_type")} className={INPUT_CLS}>
                <option value="">Select an occasion</option>
                <option value="wedding">Wedding</option>
                <option value="birthday">Birthday</option>
                <option value="engagement">Engagement</option>
                <option value="corporate">Corporate Event</option>
                <option value="flowers">Flower Order</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.2em] text-sage" htmlFor="enquiry-message">
                Tell us more *
              </label>
              <textarea
                id="enquiry-message"
                data-testid="enquiry-message-input"
                required
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder="Date, venue, colours you love, budget — anything that helps us plan."
                className="w-full border border-forest/20 bg-transparent px-4 py-3 text-sm text-forest placeholder:text-sage/60 focus:outline-none focus:ring-1 focus:ring-forest transition-colors duration-300"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="enquiry-form-submit"
            className="mt-8 flex w-full items-center justify-center gap-3 bg-forest py-4 text-xs font-bold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:bg-marigold hover:text-forest disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? "Sending..." : "Send Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
